import { useMemo, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import type { InsightsTimeRange } from '../../insights/insightsTimeRange';
import { toISODate, getInsightsTimeRangePresets } from '../../insights/insightsTimeRange';

const inputClassName =
  'px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground';

export type InsightsTimeRangeControlsProps = {
  timeRange: InsightsTimeRange;
  onTimeRangeChange: Dispatch<SetStateAction<InsightsTimeRange>>;
};

export function InsightsTimeRangeControls({
  timeRange,
  onTimeRangeChange,
}: InsightsTimeRangeControlsProps) {
  const { t } = useTranslation();

  const presets = useMemo(() => getInsightsTimeRangePresets(), []);

  const onDateChange = useCallback(
    (key: 'start' | 'end', value: string) => {
      const date = new Date(value);
      onTimeRangeChange((prev) => ({ ...prev, [key]: toISODate(date) }));
    },
    [onTimeRangeChange]
  );

  const startInputValue = useMemo(
    () => new Date(timeRange.start).toISOString().slice(0, 10),
    [timeRange.start]
  );
  const endInputValue = useMemo(
    () => new Date(timeRange.end).toISOString().slice(0, 10),
    [timeRange.end]
  );

  return (
    <div className="flex w-full flex-col gap-2 items-end sm:w-auto">
      <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">{t('stats.filters.from')}</span>
          <input
            type="date"
            value={startInputValue}
            onChange={(e) => onDateChange('start', e.target.value)}
            className={inputClassName}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">{t('stats.filters.to')}</span>
          <input
            type="date"
            value={endInputValue}
            onChange={(e) => onDateChange('end', e.target.value)}
            className={inputClassName}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <ButtonV2
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onTimeRangeChange(presets.monthToDate)}
        >
          {t('stats.filters.shortcuts.month_to_date')}
        </ButtonV2>
        <ButtonV2
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onTimeRangeChange(presets.last30Days)}
        >
          {t('stats.filters.shortcuts.last_30_days')}
        </ButtonV2>
        <ButtonV2
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onTimeRangeChange(presets.currentYear)}
        >
          {t('stats.filters.shortcuts.current_year')}
        </ButtonV2>
        <ButtonV2
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onTimeRangeChange(presets.lastYear)}
        >
          {t('stats.filters.shortcuts.last_year')}
        </ButtonV2>
      </div>
    </div>
  );
}

export default InsightsTimeRangeControls;
